import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Boxes,
  Plus,
  Search,
  Pencil,
  Layers3,
  Trash2,
  ArrowLeft,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { DataTable } from "@/components/ui/table";
import TablePagination from "@/components/common/TablePagination";
import TableText from "@/components/common/TableText";

import {
  deleteSubModule,
  getSubModules,
} from "@/redux/Modules/subModules/actions";
import AddSubModuleModal from "../modals/AddSubModuleModal";
import ConfirmModal from "@/components/ui/CelebrationButton/ConfirmModal/ConfirmModal";
import { SelectInput } from "../components/FormControls";
import { useNavigate, useParams } from "react-router-dom";
import { getBaseModuleDropdown } from "@/redux/Modules/baseModules/actions";
import { getModuleDropdownList } from "@/redux/Modules/modules/actions";
import { callLoader } from "@/redux/Loader/actions";

const SubModulesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { baseModuleId, moduleId } = useParams();

  const { loading, subModulesList, pagination } = useSelector(
    (state) => state.subModules,
  );
  console.log("subModulesList: ", subModulesList);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ======================================================
  // COLUMNS
  // ======================================================

  const columns = [
    {
      key: "SUBMODULE_NAME",
      label: "Sub Module",
      width: 320,
      sticky: "left",

      render: (row) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-teal-500/10 flex items-center justify-center">
            <Boxes className="w-5 h-5 text-teal-500" />
          </div>

          <div className="min-w-0 w-0 flex-1">
            <TableText
              title={row.SUBMODULE_NAME}
              subtitle={row.SUBMODULE_DESCRIPTION || "No description"}
              multiline
              maxLines={2}
            />
          </div>
        </div>
      ),
    },

    {
      key: "MODULE_NAME",
      label: "Module",
      width: 220,

      render: (row) => (
        <div className="flex items-center gap-2">
          <Layers3 className="w-4 h-4 text-blue-500 shrink-0" />

          <TableText title={row.MODULE_NAME || "-"} />
        </div>
      ),
    },

    {
      key: "BASEMODULE_NAME",
      label: "Base Module",
      width: 220,

      render: (row) => (
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {row.BASEMODULE_NAME || "-"}
        </span>
      ),
    },

    {
      key: "SUBMODULE_TYPE",
      label: "Type",
      width: 120,

      render: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.SUBMODULE_TYPE
              ? "bg-purple-500/10 text-purple-600 dark:text-purple-400"
              : "bg-slate-500/10 text-slate-600 dark:text-slate-400"
          }`}
        >
          {row.SUBMODULE_TYPE || "N/A"}
        </span>
      ),
    },

    {
      key: "SUBMODULE_BASEPRICE",
      label: "Base Price",
      width: 130,

      render: (row) => (
        <div className="font-semibold text-slate-900 dark:text-white">
          ${row.SUBMODULE_BASEPRICE || 0}
        </div>
      ),
    },

    {
      key: "SUBMODULE_BASEPRICETYPE",
      label: "Pricing Type",
      width: 150,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          {row.SUBMODULE_BASEPRICETYPE || "-"}
        </span>
      ),
    },

    {
      key: "SUBMODULE_PERSEATPRICE",
      label: "Per Seat",
      width: 120,

      render: (row) => (
        <span className="font-medium text-slate-700 dark:text-slate-300">
          ${row.SUBMODULE_PERSEATPRICE || 0}
        </span>
      ),
    },

    {
      key: "SUBMODULE_USAGERATE",
      label: "Usage Rate",
      width: 120,

      render: (row) => (
        <span className="font-medium text-slate-700 dark:text-slate-300">
          ${row.SUBMODULE_USAGERATE || 0}
        </span>
      ),
    },

    {
      key: "SUBMODULE_TRIAL_DAYS",
      label: "Trial",
      width: 100,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          {row.SUBMODULE_TRIAL_DAYS || 0} Days
        </span>
      ),
    },

    {
      key: "SUBMODULE_IS_ACTIVE",
      label: "Status",
      width: 120,

      render: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.SUBMODULE_IS_ACTIVE
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          {row.SUBMODULE_IS_ACTIVE ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      key: "actions",
      label: "Actions",
      width: 120,
      sticky: "right",

      render: (row) => (
        <div className="flex items-center gap-2 justify-end">
          {/* Edit */}

          <button
            onClick={() => handleEdit(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4 text-blue-500" />
          </button>

          {/* Toggle Status */}

          <button
            onClick={() => handleDeleteClick(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Toggle Status"
          >
            <Trash2 className="w-4 h-4 text-amber-500" />
          </button>
        </div>
      ),
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSubModule, setSelectedSubModule] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  console.log("confirmOpen: ", confirmOpen);
  const [baseModulesDropdown, setBaseModulesDropdown] = useState([]);
  const [modules, setModules] = useState([]);

  const [selectedBaseModuleId, setSelectedBaseModuleId] = useState("0");
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  // ======================================================
  // FETCH
  // ======================================================

  const fetchSubModules = async () => {
    try {
      const requestPayload = {
        iPageNo: currentPage,
        iPageRecords: pageSize,
        SearchKeyword: debouncedSearch,
        ModuleId: Number(moduleId || 0),
      };

      dispatch(getSubModules(requestPayload));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!moduleId) return;
    fetchSubModules();
  }, [currentPage, pageSize, debouncedSearch, moduleId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const loadDropdown = async () => {
      const res = await dispatch(getBaseModuleDropdown());
      setBaseModulesDropdown(res);
    };

    loadDropdown();
  }, []);

  useEffect(() => {
    if (!baseModuleId || baseModuleId === "0") return;

    const loadModules = async () => {
      try {
        const res = await dispatch(getModuleDropdownList(Number(baseModuleId)));

        setModules(res);
      } catch (err) {
        console.error(err);
      }
    };

    loadModules();
  }, [baseModuleId, dispatch]);

  // ======================================================
  // HANDLERS
  // ======================================================

  const handleAddSubModule = () => {
    setSelectedSubModule(null);
    setIsEdit(false);

    setSelectedSubModule(true);

    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSubModule(null);
    setIsEdit(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleEdit = (row) => {
    setSelectedSubModule(row);
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedSubModule(row);
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    try {
      const payload = {
        SUBMODULE_ID: selectedSubModule?.SUBMODULE_ID,
        DELETED_BY: "admin", // replace with logged-in user
      };

      await dispatch(deleteSubModule(payload));

      dispatch(showToast("Sub module deleted successfully", "success", true));

      setConfirmOpen(false);

      // refresh list if needed
      // dispatch(getSubModules(...));
    } catch (error) {
      dispatch(
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete sub module",
          "error",
          true,
        ),
      );
    }
  };

  // ======================================================
  // FILTERED COLUMNS
  // ======================================================

  const filteredColumns = columns.filter(
    (column) =>
      visibleColumns.length === 0 || visibleColumns.includes(column.key),
  );

  // ======================================================
  // RETURN
  // ======================================================

  return (
    <>
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
        <div className="space-y-4">
          {/* ================================================= */}
          {/* HEADER */}
          {/* ================================================= */}

          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <button
                onClick={() =>
                  navigate(`/admin/base_modules/${baseModuleId}/modules`)
                }
                className="mt-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Go Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Boxes className="w-7 h-7 text-teal-500" />
                  Sub Modules
                </h1>

                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  Manage sub modules, pricing, and configurations
                </p>
              </div>
            </div>

            <button
              onClick={handleAddSubModule}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Sub Module
            </button>
          </div>

          {/* ================================================= */}
          {/* SEARCH */}
          {/* ================================================= */}

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative w-full md:w-96">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search sub modules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
                <span className="font-bold text-slate-900 dark:text-white">
                  {pagination?.totalRecord ?? 0} sub modules found
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <SelectInput
                name="BASEMODULE_ID"
                value={baseModuleId || "0"}
                onValueChange={(name, value) => {
                  navigate(
                    `/admin/base_modules/${value}/modules/${moduleId || 0}/sub_modules`,
                  );
                }}
                placeholder="Select Base Module"
                className="!py-2"
                options={[
                  { value: "0", label: "All Base Modules" }, // important
                  ...baseModulesDropdown.map((item) => ({
                    value: item.VALUE,
                    label: item.TEXT,
                  })),
                ]}
              />
              <SelectInput
                name="MODULE_ID"
                value={moduleId || "0"}
                onValueChange={(name, value) => {
                  setSelectedModuleId(value);
                  navigate(
                    `/admin/base_modules/${baseModuleId || 0}/modules/${value}/sub_modules`,
                  );
                }}
                placeholder="Select Base Module"
                className="!py-2"
                options={[
                  { value: "0", label: "All Base Modules" }, // important
                  ...modules.map((item) => ({
                    value: item.VALUE,
                    label: item.TEXT,
                  })),
                ]}
              />
              <ColumnVisibilityDropdown
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                storageKey="sub-modules-table-columns"
              />
            </div>
          </div>

          {/* ================================================= */}
          {/* TABLE */}
          {/* ================================================= */}

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <DataTable
              data={subModulesList}
              columns={filteredColumns}
              loading={loading}
              emptyText="No Sub Modules found"
              Icon={Boxes}
            />

            {/* ================================================= */}
            {/* PAGINATION */}
            {/* ================================================= */}

            <div className="p-4">
              <TablePagination
                currentPage={currentPage}
                totalPages={pagination?.totalPage || 1}
                totalRecords={pagination?.totalRecord || 0}
                currentRecord={pagination?.currentRecord || 0}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>
      <AddSubModuleModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        isEdit={isEdit}
        editData={selectedSubModule}
        onSuccess={fetchSubModules}
      />

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmAction}
        confirmText="Delete"
        title="Delete Sub Module"
        message={
          <>
            Are you sure you want to delete{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {selectedSubModule?.SUBMODULE_NAME}
            </span>
            ?
          </>
        }
        confirmVariant="danger"
      />
    </>
  );
};

export default SubModulesPage;
